// Lightweight "Add to Home Screen" banner.
//
// On Chromium browsers we capture the `beforeinstallprompt` event and trigger
// the native install prompt on tap. iOS Safari has no such event, so there we
// fall back to a short manual hint ("tap Share, then Add to Home Screen").
//
// The banner never nags: it stays hidden once the app is already installed
// (standalone display mode) or after the user dismisses it (remembered in
// localStorage).

import { track, EVENTS } from './analytics.js';
import { t } from './i18n.js';
import { getStored, setStored, isIos } from './utils.js';

const DISMISS_KEY = 'peekababy:install-dismissed';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

const isDismissed = () => getStored(DISMISS_KEY) === '1';
const rememberDismissed = () => setStored(DISMISS_KEY, '1');

// iOS running Safari — not Chrome/Firefox/Edge on iOS, which can't add to the
// home screen anyway.
function isIosSafari() {
  const ua = navigator.userAgent;
  return isIos() && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

export function initInstallBanner() {
  if (isStandalone() || isDismissed()) return;

  const banner = document.getElementById('install-banner');
  if (!banner) return;

  const text = banner.querySelector('.install-text');
  const installBtn = banner.querySelector('.install-action');
  const dismissBtn = banner.querySelector('.install-dismiss');

  const show = () => banner.classList.add('show');
  const hide = () => banner.classList.remove('show');

  dismissBtn.addEventListener('click', () => {
    rememberDismissed();
    hide();
  });

  // Chromium: defer the browser's mini-infobar and drive it from our button.
  let deferredPrompt = null;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    installBtn.hidden = false;
    show();
    track(EVENTS.PWA_INSTALL_PROMPT_SHOWN, { platform: 'chromium' });
  });

  installBtn.addEventListener('click', async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (e) {
      /* ignore */
    }
    deferredPrompt = null;
    hide();
  });

  window.addEventListener('appinstalled', () => {
    track(EVENTS.PWA_INSTALLED);
    rememberDismissed();
    hide();
  });

  // iOS Safari can't fire beforeinstallprompt, so show manual instructions.
  if (isIosSafari()) {
    text.textContent = t('install.iosHint');
    installBtn.hidden = true;
    show();
    track(EVENTS.PWA_INSTALL_PROMPT_SHOWN, { platform: 'ios' });
  }
}
